/**
 * AuditSessionRunner — records how long each signed-in person uses the app.
 *
 * Mounted once in the Shell, above the routes, so it covers whichever screen is
 * showing and sees every signed-in account. It renders nothing.
 *
 * What it does, every 45 seconds: ask `decideBeat` whether this person is using
 * the app right now (tab visible, touched within five minutes) and, if so, start
 * a session row or bump the one it already has. The row is remembered in
 * localStorage, not memory, so a reload or a second tab carries on the SAME
 * session instead of opening one per refresh.
 *
 * Why input events and not just a timer: a timer cannot tell a person from a
 * monitor on a wall. The listeners only stamp a timestamp — no state, no
 * re-render — so they cost nothing on a screen full of scans.
 *
 * Ambient: every failure is swallowed. Recording who used the app must never
 * stand between somebody and their work, so a SharePoint hiccup just means one
 * beat is missed.
 */

import { useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { auditLogService } from "../../lib/sp/AuditLogService";
import { AUDIT_BEAT_MS, decideBeat, type IStoredSession } from "../../lib/sp/auditSession";

const STORE_KEY = "ish:audit-session";
const ACTIVITY_EVENTS = ["pointerdown", "pointermove", "keydown", "wheel", "scroll", "touchstart"] as const;

function readStored(): IStoredSession | null {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Partial<IStoredSession>;
    return typeof s.email === "string" && typeof s.rowId === "number" && typeof s.lastBeatMs === "number"
      ? { email: s.email, rowId: s.rowId, lastBeatMs: s.lastBeatMs, beats: Number(s.beats) || 1 }
      : null;
  } catch { return null; }
}

function writeStored(s: IStoredSession | null): void {
  try {
    if (s) localStorage.setItem(STORE_KEY, JSON.stringify(s));
    else localStorage.removeItem(STORE_KEY);
  } catch { /* storage full / disabled — the next beat just starts a new row */ }
}

export function AuditSessionRunner(): null {
  const { accounts } = useMsal();
  const email = (accounts[0]?.username ?? "").trim().toLowerCase();
  const name = accounts[0]?.name ?? "";

  useEffect(() => {
    if (!email) return;
    // Opening the app is itself a touch.
    let lastActivityMs = Date.now();
    const touch = () => { lastActivityMs = Date.now(); };
    for (const e of ACTIVITY_EVENTS) window.addEventListener(e, touch, { passive: true, capture: true });

    let busy = false;
    const tick = async () => {
      if (busy) return; // a slow write must not stack a second one behind it
      busy = true;
      try {
        const nowMs = Date.now();
        const stored = readStored();
        const what = decideBeat({
          nowMs, lastActivityMs, email, stored,
          visible: document.visibilityState === "visible",
        });
        if (what === "skip") return;
        if (what === "update" && stored) {
          const beats = stored.beats + 1;
          try {
            await auditLogService.beat(stored.rowId, beats);
            writeStored({ ...stored, lastBeatMs: nowMs, beats });
          } catch (err) {
            // The row was deleted out from under us: forget it so the next beat
            // starts a fresh one, rather than failing against a ghost for ever.
            if (/\b404\b/.test(String((err as Error)?.message ?? err))) writeStored(null);
            throw err;
          }
        } else {
          const rowId = await auditLogService.startSession(email, name);
          writeStored({ email, rowId, lastBeatMs: nowMs, beats: 1 });
        }
      } catch { /* ambient */ }
      finally { busy = false; }
    };

    void tick();
    const timer = window.setInterval(tick, AUDIT_BEAT_MS);
    // Coming back to the tab is a beat of its own, so the first thing after a
    // break does not wait up to 45 s to be counted.
    const onVisible = () => { if (document.visibilityState === "visible") void tick(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      for (const e of ACTIVITY_EVENTS) window.removeEventListener(e, touch, { capture: true });
    };
  }, [email, name]);

  return null;
}
